import type { ApiResponse } from "../../../../../services/apiService";
import {
  Claim,
  EdiObject,
  ManualPaymentFields,
  PossibleDuplicatePayment,
} from "../../../../../types/claims";
import { formatDisplayDate } from "../../../../helpers/dataHelper";
import {
  ClaimEditError,
  PAYER_TIER_LABELS,
  formatFeeAmount,
  parseClaimEditError,
} from "../claimAppeals";
import {
  getManualPaymentFields,
  getPaymentPayerTier,
  isManualPayment,
} from "../record-payment/manualPayments";

// Contract: app-backend api/services/manual_payments.py join_manual_payment and
// api/services/manual_payment_matching.py (REV-2585)

/** One line for a payment row (835 or manual): "Primary payer · $250.00 · Sep 28, 2026". */
export const summarizePayment = (ediObj: EdiObject): string => {
  const fields = getManualPaymentFields(ediObj);
  return [
    PAYER_TIER_LABELS[fields.payerTier],
    fields.outcome === "paid" ? formatFeeAmount(fields.amount) : "Denied",
    fields.paymentDate ? formatDisplayDate(fields.paymentDate) : "",
  ]
    .filter(Boolean)
    .join(" · ");
};

/** An 835 that arrived electronically, the only kind a manual payment can be joined to. */
export const isElectronicRemit = (ediObj: EdiObject): boolean =>
  ediObj.objectType === "PAYMENT" &&
  !isManualPayment(ediObj) &&
  !!ediObj.attachmentId;

export const findByAttachmentId = (
  ediObjects: EdiObject[],
  attachmentId: string,
): EdiObject | undefined =>
  ediObjects.find((ediObj) => ediObj.attachmentId === attachmentId);

export interface RemitCandidate {
  remit: EdiObject;
  /** Why it is a likely match ("Same check / EFT #"…); empty when it isn't one. */
  matchReasons: string[];
  /** It already has a manual payment joined, and the API allows only one. */
  isJoined: boolean;
}

const sameText = (a: string, b: string) => !!a.trim() && a.trim() === b.trim();

/** What a manual payment and a remit have in common, the backend's matching rules first. */
export const getMatchReasons = (
  manual: EdiObject,
  remit: EdiObject,
): string[] => {
  const m = getManualPaymentFields(manual);
  const r = getManualPaymentFields(remit);
  const reasons: string[] = [];
  if (sameText(m.checkOrEftNumber, r.checkOrEftNumber))
    reasons.push("Same check / EFT #");
  if (sameText(m.payerClaimNumber, r.payerClaimNumber))
    reasons.push("Same payer claim #");
  if (m.amount !== "" && Number(m.amount) === Number(r.amount))
    reasons.push("Same amount");
  return reasons;
};

// Finite, so two missing dates tie instead of subtracting to NaN
const NO_DATE = Number.MAX_SAFE_INTEGER;

const daysApart = (a: string, b: string) => {
  const diff = Math.abs(new Date(a).getTime() - new Date(b).getTime());
  return isNaN(diff) ? NO_DATE : diff;
};

/**
 * The claim's electronic remits a manual payment can be joined to: same payer
 * tier only (the API refuses others). A remit a duplicate warning names for this
 * payment comes first, then likely matches, then the closest payment date;
 * remits that already have a payment joined go last.
 */
export const getJoinCandidates = (
  manual: EdiObject,
  ediObjects: EdiObject[],
  warnings: PossibleDuplicatePayment[] = [],
): RemitCandidate[] => {
  const tier = getPaymentPayerTier(manual);
  const manualDate = getManualPaymentFields(manual).paymentDate;
  const warned = new Set(
    warnings
      .filter((warning) => warning.manualAttachmentId === manual.attachmentId)
      .map((warning) => warning.remitAttachmentId),
  );
  const isWarned = (remit: EdiObject) =>
    !!remit.attachmentId && warned.has(remit.attachmentId);

  return ediObjects
    .filter(
      (ediObj) =>
        isElectronicRemit(ediObj) && getPaymentPayerTier(ediObj) === tier,
    )
    .map((remit) => ({
      remit,
      matchReasons: getMatchReasons(manual, remit),
      isJoined: !!remit.matchedManual,
    }))
    .sort(
      (a, b) =>
        Number(a.isJoined) - Number(b.isJoined) ||
        Number(isWarned(b.remit)) - Number(isWarned(a.remit)) ||
        b.matchReasons.length - a.matchReasons.length ||
        daysApart(manualDate, getManualPaymentFields(a.remit).paymentDate) -
          daysApart(manualDate, getManualPaymentFields(b.remit).paymentDate),
    );
};

const PR_GROUPS = ["PATIENT_RESPONSIBILITY", "PR"];

const sumPatientResponsibility = (adjustments: unknown): number =>
  (Array.isArray(adjustments) ? adjustments : []).reduce(
    (total: number, adjustment: Record<string, unknown>) => {
      const group = String(adjustment?.group ?? adjustment?.groupCode ?? "");
      const amount = Number(adjustment?.amount);
      return PR_GROUPS.includes(group) && !isNaN(amount) ? total + amount : total;
    },
    0,
  );

/**
 * An 835's fields as the join summary shows them. Unlike a manual entry, a real
 * 835 carries patient responsibility on its service lines too, so it is summed
 * over the claim and every line, as app-backend's status calculator does.
 */
export const getRemitFields = (remit: EdiObject): ManualPaymentFields => {
  const obj = remit as Record<string, unknown>;
  const lines = Array.isArray(obj.serviceLines)
    ? (obj.serviceLines as Record<string, unknown>[])
    : [];
  const total =
    sumPatientResponsibility(obj.adjustments) +
    lines.reduce((sum, line) => sum + sumPatientResponsibility(line?.adjustments), 0);
  return {
    ...getManualPaymentFields(remit),
    patientResponsibility: total > 0 ? total.toFixed(2) : "",
  };
};

/** A possible-duplicate warning with the two payments it names, when they're still on the claim. */
export interface DuplicatePaymentPair {
  warning: PossibleDuplicatePayment;
  manual?: EdiObject;
  remit?: EdiObject;
}

export const resolveDuplicatePayments = (
  warnings: PossibleDuplicatePayment[],
  ediObjects: EdiObject[],
): DuplicatePaymentPair[] =>
  warnings.map((warning) => ({
    warning,
    manual: findByAttachmentId(ediObjects, warning.manualAttachmentId),
    remit: findByAttachmentId(ediObjects, warning.remitAttachmentId),
  }));

/**
 * The claim's paid amount once the manual payment is joined: it stops counting
 * and the remit already counts, so the API's sum just loses the manual amount.
 * A decimal string, like the FeeChange values.
 */
export const getPaidAmountAfterJoin = (
  claim: Claim,
  manual: EdiObject,
): string | null => {
  const paid = Number(claim.paidAmount);
  const amount = Number(getManualPaymentFields(manual).amount);
  if (claim.paidAmount == null || isNaN(paid) || isNaN(amount)) return null;
  return (paid - amount).toFixed(2);
};

export type JoinPaymentErrorCode =
  | "validation_error"
  | "not_found"
  | "payment_not_active"
  | "remit_already_joined"
  | "claim_invoiced";

export type JoinPaymentError = ClaimEditError<
  JoinPaymentErrorCode,
  "remitAttachmentId"
>;

export const parseJoinPaymentError = (
  response: ApiResponse<unknown>,
): JoinPaymentError =>
  parseClaimEditError(
    response,
    [
      "validation_error",
      "not_found",
      "payment_not_active",
      "remit_already_joined",
      "claim_invoiced",
    ],
    ["remitAttachmentId"],
  );
